import React, { useContext, useEffect, useMemo, useState } from "react";

import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";

import { useNotificationPreferences } from "@domains/notification/hooks/useNotificationPreferences";
import type { NotificationPreferences } from "@domains/notification/types";
import { NotificationContext } from "src/shell/contexts/notificationContext";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

interface PreferencesSettingsProps {
  /**
   * Optional override for the preferences hook result. When provided (used in
   * tests), the real hook is still called unconditionally to satisfy the Rules
   * of Hooks, but its return value is discarded in favor of this override.
   */
  preferencesHook?: ReturnType<typeof useNotificationPreferences>;
}

const PreferencesSettings: React.FC<PreferencesSettingsProps> = ({
  preferencesHook,
}) => {
  const theme = useTheme();
  const { setSnackBarNotification } = useContext(NotificationContext);
  // Always call the real hook unconditionally to satisfy the Rules of Hooks.
  const realHook = useNotificationPreferences();
  const hook = preferencesHook ?? realHook;
  const { preferences, loading, error, updatePreferences } = hook;

  // Local editable copy so toggles are immediate; synced from API on load.
  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (preferences) {
      setDraft({ ...preferences });
    }
  }, [preferences]);

  const hasChanges = useMemo(() => {
    if (!draft || !preferences) return false;
    return (
      draft.emailEnabled !== preferences.emailEnabled ||
      draft.pushEnabled !== preferences.pushEnabled ||
      draft.resourceEvents !== preferences.resourceEvents ||
      draft.billingEvents !== preferences.billingEvents ||
      draft.securityEvents !== preferences.securityEvents
    );
  }, [draft, preferences]);

  const handleToggle = (key: keyof NotificationPreferences) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: !draft[key] });
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const result = await updatePreferences(draft);
      if (result) {
        setSnackBarNotification({
          message: "Notification preferences saved",
          severity: "success",
        });
      } else {
        setSnackBarNotification({
          message: "Failed to save notification preferences",
          severity: "error",
        });
      }
    } catch {
      setSnackBarNotification({
        message: "Failed to save notification preferences",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !draft) {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <NotificationsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Notifications
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
        </Stack>
      </Box>
    );
  }

  if (error && !draft) {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="body2" color="error.main">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!draft) return null;

  return (
    <>
      {/* Notifications */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <NotificationsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Notifications
          </Typography>
        </Stack>

        <Stack spacing={2.5}>
          <FormControlLabel
            control={
              <Switch
                checked={draft.emailEnabled}
                onChange={() => handleToggle("emailEnabled")}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Email notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive email notifications for important updates
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={draft.pushEnabled}
                onChange={() => handleToggle("pushEnabled")}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Push notifications
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive push notifications in the browser
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={draft.resourceEvents}
                onChange={() => handleToggle("resourceEvents")}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Resource events
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Provisioning, state changes, and resource lifecycle events
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={draft.billingEvents}
                onChange={() => handleToggle("billingEvents")}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Billing events
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan changes, usage thresholds, and invoices
                </Typography>
              </Box>
            }
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={draft.securityEvents}
                onChange={() => handleToggle("securityEvents")}
                color="primary"
                size="small"
              />
            }
            label={
              <Box sx={{ ml: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  Security events
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Login attempts, token activity, and access changes
                </Typography>
              </Box>
            }
          />
        </Stack>

        {/* Save */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            onClick={() => void handleSave()}
            disabled={saving || loading || !hasChanges}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default PreferencesSettings;
